import { z } from 'zod';

/** Error taxonomy shared by every package, HTTP response and receipt. */
export const ErrorCode = z
  .enum([
    'validation_failed',
    'unauthorized',
    'forbidden',
    'policy_denied',
    'approval_required',
    'approval_invalid',
    'idempotency_conflict',
    'not_found',
    'conflict',
    'timeout',
    'downstream_failed',
    'outcome_unknown',
    'limit_exceeded',
    'kill_switch',
    'internal',
    'step_up_required',
    /** Added by 09: a workflow run's recorded version has no handler in any worker (after the grace period). */
    'workflow_version_missing',
    /**
     * The password was right but the account has a pending one-time setup (e.g. Keycloak required action
     * `UPDATE_PASSWORD` after a temporary password). A password grant cannot complete it; use company SSO.
     */
    'account_setup_required',
    /** Added by 17a: a business operation is claimed by another in-flight attempt. */
    'operation_conflict',
    /** Added by 17a: a business operation exists with a different payload hash. */
    'operation_payload_mismatch',
    /** Added by 17a: a business operation's outcome is uncertain; reconcile before any retry or reissue. */
    'operation_uncertain',
    /** Added by 17a: a new approval request for a (gate, subject revision) that was already rejected. */
    'approval_subject_rejected',
    /** Added by 17b2: the request uses a contract path that is declared but not wired yet (e.g. action subjects). */
    'not_implemented',
    /**
     * Added by 17b1: a step output or signal payload is above the workflow engine's limit (`maxStepOutputBytes`,
     * `maxSignalBytes`; default 256 KiB). Put large data in object storage and pass a reference.
     */
    'payload_too_large',
    /**
     * Added by 17b1 (review): a run already buffers `maxBufferedSignalBytes` of delivered, unconsumed signals
     * (default 1 MiB). Retryable once the run consumes some of them.
     */
    'signal_buffer_full',
    /**
     * Added by 17c0: a pack evaluation (`<module>.<name>`) or pack suite kind clashes with a platform evaluation
     * name or pattern, or with another entry already in the evaluation catalog.
     */
    'evaluation_name_conflict',
    /**
     * Added by 21 step 0 (brief 21 §1b.10): the tool is not in the `tools` scope of the calling activation's pinned
     * definition (or above its `maxAutonomy`), or a spawn's `toolScope` is not a subset of it. Ledgered.
     */
    'tool_out_of_scope',
    /** Added by 21 step 0 (§1b.4): a `RecoveryDecision` misses (or invents) an entry for a completed receipt. */
    'recovery_effects_incomplete',
    /**
     * Added by 21 step 0 (§1b.4): a timeline/ledger code recorded when a receipt's tool has no `compensate`; `recover`
     * does not throw it — the entry becomes `stand` plus a manual-reversal obligation (op-08).
     */
    'compensation_unavailable',
    /**
     * Added by 23: a model binding pins a connection `configRevision` that is no longer configured. The run blocks
     * instead of switching endpoint; `details` names `connectionId`, `pinnedRevision` and `configuredRevision`.
     */
    'connection_revision_unavailable',
    /** Added by 23: the pinned catalogue entry was revoked; every further dispatch is denied (no fallback around it). */
    'model_revoked',
    /**
     * Added by 23: a legacy in-flight attempt already made model calls but has no durable model binding; further model
     * calls are blocked with a migration explanation and a new episode attempt is required.
     */
    'model_binding_missing',
    /**
     * Added by 26a (brief 26 §3.6): a gap without an owner role, or an owner role with no binding in strict mode
     * (warn mode writes a ledger warning instead and owns the gap on the resolution owner / case creator).
     */
    'obligation_unowned',
    /** Added by 26a: an answer to a met or waived obligation without `revises`, or to an abandoned case. */
    'obligation_settled',
    /** Added by 26a: a definition or agent without a resolution where one is required (strict mode). */
    'resolution_undefined',
    /** Added by 26a: the resolution evaluator threw or a referenced record is gone; the case state is unchanged. */
    'resolution_unavailable',
  ])
  .meta({ id: 'ErrorCode' });
export type ErrorCode = z.infer<typeof ErrorCode>;

/** HTTP status used when a PlatformError with this code crosses the HTTP boundary. */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = {
  validation_failed: 400,
  unauthorized: 401,
  forbidden: 403,
  policy_denied: 403,
  approval_required: 409,
  approval_invalid: 409,
  idempotency_conflict: 409,
  not_found: 404,
  conflict: 409,
  timeout: 504,
  downstream_failed: 502,
  outcome_unknown: 502,
  limit_exceeded: 429,
  kill_switch: 423,
  internal: 500,
  step_up_required: 403,
  workflow_version_missing: 409,
  account_setup_required: 403,
  operation_conflict: 409,
  operation_payload_mismatch: 409,
  operation_uncertain: 409,
  approval_subject_rejected: 409,
  not_implemented: 501,
  payload_too_large: 413,
  signal_buffer_full: 413,
  evaluation_name_conflict: 409,
  tool_out_of_scope: 403,
  recovery_effects_incomplete: 400,
  compensation_unavailable: 409,
  connection_revision_unavailable: 409,
  model_revoked: 409,
  model_binding_missing: 409,
  obligation_unowned: 409,
  obligation_settled: 409,
  resolution_undefined: 400,
  resolution_unavailable: 503,
};

/** Codes that are retryable unless the thrower says otherwise. */
export const RETRYABLE_BY_DEFAULT: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'timeout',
  'downstream_failed',
]);

export interface PlatformErrorOptions {
  details?: Record<string, unknown>;
  retryable?: boolean;
  cause?: unknown;
}

/**
 * The only error type the platform throws on purpose. Never throw strings.
 * `retryable` defaults to true for `timeout` and `downstream_failed`, false otherwise.
 */
export class PlatformError extends Error {
  readonly code: ErrorCode;
  readonly details?: Record<string, unknown>;
  readonly retryable: boolean;

  constructor(code: ErrorCode, message: string, options: PlatformErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'PlatformError';
    this.code = code;
    if (options.details !== undefined) this.details = options.details;
    this.retryable = options.retryable ?? RETRYABLE_BY_DEFAULT.has(code);
  }

  /** HTTP status for this error's code. */
  get status(): number {
    return ERROR_HTTP_STATUS[this.code];
  }

  toJSON(): ErrorPayload {
    return {
      code: this.code,
      message: this.message,
      retryable: this.retryable,
      ...(this.details === undefined ? {} : { details: this.details }),
    };
  }

  /**
   * Rebuilds a PlatformError from an HTTP error body (used by API clients). 24: the body's `traceId` (the server's
   * reference for the log line) is kept as `details.traceId`, as the api-client does.
   */
  static fromBody(body: ErrorBody): PlatformError {
    const { details, traceId } = body.error;
    const merged = traceId === undefined ? details : { ...(details ?? {}), traceId };
    return new PlatformError(body.error.code, body.error.message, {
      retryable: body.error.retryable,
      ...(merged === undefined ? {} : { details: merged }),
    });
  }
}

/** True when `value` is a PlatformError (also across realms / duplicated module copies). */
export function isPlatformError(value: unknown): value is PlatformError {
  return (
    value instanceof PlatformError ||
    (typeof value === 'object' &&
      value !== null &&
      (value as { name?: unknown }).name === 'PlatformError' &&
      typeof (value as { code?: unknown }).code === 'string')
  );
}

/** Wraps any thrown value into a PlatformError (`internal` unless it already is one). */
export function toPlatformError(value: unknown): PlatformError {
  if (isPlatformError(value)) return value;
  const message = value instanceof Error ? value.message : String(value);
  return new PlatformError('internal', message, { cause: value });
}

export const ErrorPayload = z
  .object({
    code: ErrorCode,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
    retryable: z.boolean(),
    traceId: z.string().optional(),
  })
  .meta({ id: 'ErrorPayload' });
export type ErrorPayload = z.infer<typeof ErrorPayload>;

/** Body of every non-2xx JSON HTTP response. */
export const ErrorBody = z.object({ error: ErrorPayload }).meta({ id: 'ErrorBody' });
export type ErrorBody = z.infer<typeof ErrorBody>;

export function toErrorBody(error: PlatformError, traceId?: string): ErrorBody {
  return { error: { ...error.toJSON(), ...(traceId === undefined ? {} : { traceId }) } };
}

/**
 * Added by 24: codes whose server-side message and details may carry internal text (driver errors, hostnames,
 * upstream bodies, stack fragments). Across the HTTP boundary they are replaced by a fixed safe message and no
 * details; the original stays in the server log under the response's `traceId`.
 */
const INTERNAL_ERROR_CODE_LIST = [
  'internal',
  'downstream_failed',
  'timeout',
  'outcome_unknown',
  'workflow_version_missing',
  'recovery_effects_incomplete',
  'compensation_unavailable',
  /** Added by 26a: evaluator errors carry pack internals. */
  'resolution_unavailable',
] as const satisfies readonly ErrorCode[];

/** Added by 24: an error code whose server-side text never crosses the HTTP boundary. */
export type InternalErrorCode = (typeof INTERNAL_ERROR_CODE_LIST)[number];

export const INTERNAL_ERROR_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>(INTERNAL_ERROR_CODE_LIST);

/** Added by 24: the fixed public message per internal code (one sentence, no interpolation). */
export const SAFE_ERROR_MESSAGES: Readonly<Record<InternalErrorCode, string>> = {
  internal: 'Something went wrong on our side.',
  downstream_failed: 'A connected system did not respond as expected.',
  timeout: 'The operation took too long to complete.',
  outcome_unknown: 'The result of this operation is not known yet.',
  workflow_version_missing: 'The process version for this work is not available.',
  recovery_effects_incomplete: 'The recovery plan does not cover every recorded effect.',
  compensation_unavailable: 'This effect cannot be reversed automatically.',
  resolution_unavailable: 'We could not check whether the case reached its outcome.',
};

/** Added by 24: true when `code` is in {@link INTERNAL_ERROR_CODES}. */
export function isInternalErrorCode(code: ErrorCode): code is InternalErrorCode {
  return INTERNAL_ERROR_CODES.has(code);
}

/**
 * Added by 24: the error payload that may leave the server. For {@link INTERNAL_ERROR_CODES} it carries the code,
 * a fixed safe message, `retryable` and `traceId` — never the original message or details. Every other code passes
 * its message and details through unchanged (they are business text written for the caller).
 */
export function publicErrorBody(error: PlatformError, traceId?: string): ErrorPayload {
  const trace = traceId === undefined ? {} : { traceId };
  const code = error.code;
  if (isInternalErrorCode(code)) {
    return {
      code,
      message: SAFE_ERROR_MESSAGES[code],
      retryable: error.retryable,
      ...trace,
    };
  }
  return { ...error.toJSON(), ...trace };
}
